import { inject, Injectable } from '@angular/core';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { getJawByToothNumber } from '@modules/teeth-diagram/models/teeth-numbering';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { bridgeUnitType } from '@shared/models/consts';
import { IdName } from '@shared/models/id-name';
import { PonticDesignEnum } from '@shared/models/pontic-design.enum';
import { LocalSettingsQuery } from '@shared/store/localSettings/local-settings-query';
import { LocalSettingsStore } from '@shared/store/localSettings/local-settings-store';
import { requireNonNull } from '@shared/utils/type-util';
import { combineLatest, defer, EMPTY, filter, map, Observable, shareReplay } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { IntuitiveIndication, IntuitiveToothForDiagram, IntuitiveToothForTreatment, TeethNumberingSystem } from './models';
import { IntuitiveBridge, IntuitiveBridgeValidationErrors } from './models/intuitive-bridge';
import { fromUnitTypeInBridge, toUnitTypeInBridge } from './models/intuitive-indication';
import { TOOTH_MODAL_ACTION, ToothModalResult } from './models/tooth-modal';
import { RxIntuitiveTeethDiagramFacade } from './rx-intuitive-teeth-diagram.facade';
import { RxIntuitiveBridgeValidationService } from './services/rx-intuitive-bridge-validation.service';
import { RxIntuitiveCopyInBridgeService } from './services/rx-intuitive-copy-in-bridge.service';

const BRIDGE_INDICATIONS_WITH_MODALS: IntuitiveIndication[] = [IntuitiveIndication.BridgeImplantBased];

@Injectable()
export class RxIntuitiveTeethDiagramBridgeFacade extends RxIntuitiveTeethDiagramFacade {
	private readonly sharingService = inject(RxIntuitiveCopyInBridgeService);
	private readonly validationService = inject(RxIntuitiveBridgeValidationService);

	private readonly localSettingsStore = inject(LocalSettingsStore);
	private readonly localSettingsQuery = inject(LocalSettingsQuery);

	readonly selectedBridge$: Observable<IntuitiveBridge> = this.intuitiveDiagramQuery.selectedBridge$.pipe(
		filter((bridge): bridge is IntuitiveBridge => bridge != null)
	);

	readonly selectedBridgeTeeth$: Observable<Tooth[]> = this.intuitiveDiagramQuery.selectedBridgeTeeth$;

	readonly hasUnsavedChanges$: Observable<boolean> = combineLatest([this.selectedBridge$, this.teethDiagramQuery.teethFlat$]).pipe(
		map(([selectedBridge, teethFlat]) => {
			const originalBridgeState = this.bridgeService.getBridgeSpanByBridgeIndex(selectedBridge.index, teethFlat);

			return originalBridgeState ? this.bridgeService.hasUnsavedChanges(selectedBridge, originalBridgeState) : false;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly bridgeListItem$ = combineLatest([
		this.selectedBridge$,
		this.teethNumberingSystem$,
		this.intuitiveDiagramQuery.toothLastTouchedAt$
	]).pipe(
		map(([bridge, teethNumberingSystem, toothLastTouchedAt]) => {
			return this.mapBridgeToDiagramListItem(bridge, teethNumberingSystem, toothLastTouchedAt);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private readonly validationError$: Observable<IntuitiveBridgeValidationErrors | undefined> = combineLatest([
		this.intuitiveDiagramQuery.isSelectedBridgeSubmitted$,
		this.selectedBridge$
	]).pipe(
		map(([isSubmitted, bridge]) => {
			if (isSubmitted) {
				return this.validationService.validateBridge(bridge);
			}

			return undefined;
		})
	);

	readonly validationErrorSummary$: Observable<string | undefined> = this.validationError$.pipe(
		map(validationErrors => {
			// Business requirements are to show only one error message at a time
			return validationErrors?.summary?.[0];
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly showGuidanceBanner$: Observable<boolean> = combineLatest([
		this.selectedBridge$,
		this.intuitiveDiagramQuery.dismissedGuidanceBanner$,
		this.localSettingsQuery.userSettings$,
		this.validationErrorSummary$,
		this.isTeethDiagramReadOnly$
	]).pipe(
		map(([, dismissed, userSettings, validationErrorSummary, isReadonly]) => {
			if (!!validationErrorSummary || dismissed || isReadonly) {
				return false;
			}

			return !userSettings?.[this.shellQuery.contactId ?? 0]?.dontShowBridgeGuidanceBanner;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * All bridge indications that are renderable for the current procedure (procedure-allowed ∩ inner∪outer position set).
	 * Independent of tooth selection — buttons that are visible but not in {@link enabledIndications$} are shown disabled.
	 */
	readonly visibleIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$
	]).pipe(
		map(([procedureMap, rxConfiguration]) => this.indicationsService.getBridgeProcedureIndications(procedureMap, rxConfiguration)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/**
	 * Subset of {@link visibleIndications$} compatible with all currently-selected teeth inside the bridge.
	 *
	 * Source of truth for per-tooth metadata (ToothInBridgeTypeID, position) is the in-store
	 * `selectedBridge.teeth` snapshot — not `teethDiagramQuery.teethRecord`, since the bridge has
	 * not yet been persisted to the diagram when the user is editing it.
	 */
	readonly enabledIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.selectedBridge$,
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$
	]).pipe(
		map(([bridge, selectedToothIds, procedureMap, rxConfiguration]) => {
			const selectedBridgeTeeth = bridge.teeth.filter(t => t.ToothID !== null && selectedToothIds.includes(t.ToothID));

			if (selectedBridgeTeeth.length === 0) {
				return [];
			}

			return this.indicationsService.getCommonBridgeIndicationsForTeeth(selectedBridgeTeeth, bridge, procedureMap, rxConfiguration);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	override readonly selectedTeethIndicationIds$: Observable<IntuitiveIndication[]> = combineLatest([
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.intuitiveDiagramQuery.selectedBridgeTeeth$
	]).pipe(
		map(([selectedToothIds, bridgeTeeth]) => {
			if (selectedToothIds.length === 0) {
				return [];
			}

			const indications = selectedToothIds
				.map(id => {
					const tooth = bridgeTeeth.find(t => t.ToothID === id);

					return tooth?.ToothInBridgeTypeID != null ? fromUnitTypeInBridge(tooth.ToothInBridgeTypeID) : null;
				})
				.filter((ind): ind is IntuitiveIndication => ind != null);

			if (indications.length === 0) {
				return [];
			}

			const first = indications[0];

			return indications.every(id => id === first) ? [first] : [];
		})
	);

	readonly upperJawBridges$: Observable<Record<number, IntuitiveBridge>> = combineLatest([
		this.teethDiagramQuery.upperJaw$,
		this.selectedBridge$
	]).pipe(
		map(([jaw, selectedBridge]) => {
			return this.bridgeService.getAllBridgesForJawIntuitive(jaw, selectedBridge);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly lowerJawBridges$: Observable<Record<number, IntuitiveBridge>> = combineLatest([
		this.teethDiagramQuery.lowerJaw$,
		this.selectedBridge$
	]).pipe(
		map(([jaw, selectedBridge]) => {
			return this.bridgeService.getAllBridgesForJawIntuitive(jaw, selectedBridge);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly upperJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.upperJaw$,
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.upperJawBridges$,
		this.selectedBridge$,
		this.validationError$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges, selectedBridge, validationError]) =>
			this.mapJawTeeth(jaw, selectedToothIds, teethNumberingSystem, bridges, selectedBridge, validationError)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly lowerJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.lowerJaw$,
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.lowerJawBridges$,
		this.selectedBridge$,
		this.validationError$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges, selectedBridge, validationError]) =>
			this.mapJawTeeth(jaw, selectedToothIds, teethNumberingSystem, bridges, selectedBridge, validationError)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly canRemoveSelectedTooth$: Observable<boolean> = combineLatest([
		this.intuitiveDiagramQuery.selectedBridge$,
		this.intuitiveDiagramQuery.selectedToothIds$
	]).pipe(
		map(([bridge, selectedToothIds]) => !!bridge && this.bridgeService.canRemoveToothFromBridge(bridge, selectedToothIds)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly bridgeEditorTeeth$: Observable<IntuitiveToothForTreatment[]> = combineLatest([
		this.intuitiveDiagramQuery.selectedBridgeTeeth$,
		this.intuitiveDiagramQuery.selectedToothIds$,
		this.teethNumberingSystem$,
		this.intuitiveDiagramQuery.toothLastTouchedAt$
	]).pipe(
		map(
			([bridgeTeeth, selectedToothIds, teethNumberingSystem, toothLastTouchedAt]: [
				Tooth[],
				number[],
				TeethNumberingSystem,
				Record<number, number>
			]) => {
				const selected = bridgeTeeth.filter(t => t.ToothID !== null && selectedToothIds.includes(t.ToothID));

				return selected
					.map(t => this.toBridgeToothForTreatment(t, teethNumberingSystem, toothLastTouchedAt))
					.sort((a, b) => b.lastTouchedAt - a.lastTouchedAt || a.toothId - b.toothId);
			}
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly canEditBridgeToothEditor$: Observable<boolean> = combineLatest([
		this.bridgeEditorTeeth$.pipe(map(editorTeeth => this.computeCanEditToothEditor(editorTeeth))),
		this.toothEditorReadOnly$
	]).pipe(map(([canEditByUnitType, readOnly]) => canEditByUnitType && !readOnly));

	readonly ponticDesigns$: Observable<IdName[]> = this.shellQuery.ponticDesigns$;

	override assignIndication(indicationId: IntuitiveIndication, skipResetTooth = false): void {
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			this.logger.warn('assignIndication called with no teeth selected', {
				module: this.facadeName
			});

			return;
		}

		const unitTypeInBridge = toUnitTypeInBridge(indicationId);

		if (unitTypeInBridge == null) {
			return;
		}

		/**
		 * For indications that use a modal, we do not automatically reset the values of selected teeth.
		 * This ensures that if the user cancels the modal, the teeth keep their previous values.
		 * Because of this, these teeth values must be reset manually in the handler of modal's save action.
		 */
		if (skipResetTooth) {
			this.intuitiveDiagramStore.updateBridgeTeethByIds(selectedToothIds, { ToothInBridgeTypeID: unitTypeInBridge });
		} else {
			this.intuitiveDiagramStore.assignBridgeIndication(selectedToothIds, unitTypeInBridge);
		}

		const updates = this.sharingService.computeBridgeIndicationSharingUpdates(
			selectedToothIds,
			unitTypeInBridge,
			this.intuitiveDiagramQuery.selectedBridgeTeeth
		);

		for (const { toothId, resetTooth } of updates) {
			this.intuitiveDiagramStore.updateBridgeTeethByIds([toothId], resetTooth);
		}

		this.intuitiveDiagramStore.markRecentlyEditedTeeth(selectedToothIds);
	}

	override toggleToothSelection(toothId: number): void {
		const selectedBridge = requireNonNull(this.intuitiveDiagramQuery.selectedBridge);
		const isInBridge = selectedBridge.teeth.some(t => t.ToothID === toothId);

		if (isInBridge) {
			this.toggleInBridgeSelection(toothId);

			return;
		}
		this.addTeethInBridge(toothId, selectedBridge);
	}

	override openModalForUnitTypeIfNeeded(indication: IntuitiveIndication): Observable<ToothModalResult> | null {
		if (this.orderQuery.isGlidewellOrder) {
			return null;
		}

		if (!BRIDGE_INDICATIONS_WITH_MODALS.includes(indication)) {
			return null;
		}

		return this.openModalForUnitTypeIfNeededInternal(indication);
	}

	saveBridge(): void {
		const currentBridge = requireNonNull(this.intuitiveDiagramQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		this.intuitiveDiagramStore.setBridgeSubmitted();

		const error = this.validationService.validateBridge(currentBridge);

		if (error.summary.length > 0 || Object.values(error.teeth).some(errors => errors.length > 0)) {
			return;
		}

		if (originalBridge) {
			const persistedTeeth = originalBridge.teeth.filter(t => t.ToothID != null).map(t => t.ToothID) as number[];

			this.teethDiagramStore.resetIndicationByIds(persistedTeeth, UnitTypes.Regular);
		}

		this.teethDiagramStore.updateTeeth({ teethToUpdate: currentBridge.teeth });

		this.intuitiveDiagramStore.deselectBridge();
	}

	revertChanges(): void {
		const currentBridge = requireNonNull(this.intuitiveDiagramQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		if (originalBridge != null) {
			return this.intuitiveDiagramStore.selectBridge(originalBridge);
		}
	}

	closeBridge(): void {
		this.intuitiveDiagramStore.deselectBridge();
	}

	updateBridgeTeethCrownData(crownForm: CrownForm): void {
		const partialToothUpdate = {
			MaterialID: crownForm.material,
			ShadeSystemId: crownForm.shadeSystem,
			ShadeBody: crownForm.shadeBody,
			SpecificationId: crownForm.specification
		};

		this.updateBridgeTeethByIds(crownForm.teethIds, partialToothUpdate);

		if (this.intuitiveDiagramQuery.selectedBridge) {
			this.sharingService.updateSharedDefaults(crownForm, this.intuitiveDiagramQuery.selectedBridge);
			this.shareCrownDataForBridgeTeeth();
		}
	}

	updateBridgeTeethPonticDesign(toothIds: number[], ponticDesignId: PonticDesignEnum | null): void {
		this.updateBridgeTeethByIds(toothIds, { PonticDesignID: ponticDesignId });
		this.shareCrownDataForBridgeTeeth();
	}

	updateBridgeTeethByIds(toothIds: number[], partialUpdate: Partial<Tooth>): void {
		this.intuitiveDiagramStore.updateBridgeTeethByIds(toothIds, partialUpdate);
		this.intuitiveDiagramStore.markRecentlyEditedTeeth(toothIds);
	}

	dismissGuidanceBanner(dontShowAgain: boolean): void {
		this.intuitiveDiagramStore.dismissGuidanceBanner();
		if (dontShowAgain) {
			this.localSettingsStore.updateLocalSettings(this.shellQuery.contactId ?? 0, { dontShowBridgeGuidanceBanner: true });
		}
	}

	deleteBridge(): void {
		const currentBridge = requireNonNull(this.intuitiveDiagramQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		if (originalBridge) {
			const resetTeethIds = originalBridge.teeth
				.filter((tooth): tooth is Tooth & { ToothID: number } => tooth.ToothID !== null)
				.map(tooth => tooth.ToothID);

			this.teethDiagramStore.resetIndicationByIds(resetTeethIds, UnitTypes.Regular);
		}

		this.sharingService.clearSharedDefaults(currentBridge);
		this.intuitiveDiagramStore.deselectBridge();
	}

	removeToothFromBridge(): void {
		const bridge = this.intuitiveDiagramQuery.selectedBridge;
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;

		if (!bridge || !selectedToothIds.length) {
			return;
		}

		const wasRemovingFromLeft = selectedToothIds.includes(bridge.outerLeftToothId as number);
		const remainingTeeth = wasRemovingFromLeft
			? bridge.teeth.slice(selectedToothIds.length)
			: bridge.teeth.slice(0, bridge.teeth.length - selectedToothIds.length);

		const newOuterLeftToothId = remainingTeeth[0].ToothID;
		const newOuterRightToothId = remainingTeeth[remainingTeeth.length - 1].ToothID;
		const newFocusToothId = wasRemovingFromLeft ? newOuterLeftToothId : newOuterRightToothId;

		const updatedBridge: IntuitiveBridge = {
			...bridge,
			outerLeftToothId: newOuterLeftToothId,
			outerRightToothId: newOuterRightToothId,
			teeth: remainingTeeth
		};

		this.intuitiveDiagramStore.update({
			selectedBridge: updatedBridge,
			selectedToothIds: newFocusToothId ? [newFocusToothId] : []
		});
	}

	shareCrownDataForBridgeTeeth(): void {
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;

		if (!selectedToothIds || selectedToothIds.length === 0) {
			this.logger.warn('change crown data called with no teeth selected', {
				module: this.facadeName
			});

			return;
		}

		const selectedTooth = this.intuitiveDiagramQuery.selectedBridgeTeeth.find(t => t.ToothID === selectedToothIds[0]);

		if (!selectedTooth) {
			this.logger.warn('change crown data called with no teeth selected', {
				module: this.facadeName
			});

			return;
		}

		const targetTeeth = this.intuitiveDiagramQuery.selectedBridgeTeeth.filter(t => t.ToothInBridgeTypeID !== null);
		const updates = this.sharingService.shareDefaulPropsToBridgeTeeth(targetTeeth, selectedTooth);

		for (const { toothId, partialToUpdate } of updates) {
			this.intuitiveDiagramStore.updateBridgeTeethByIds([toothId], partialToUpdate);
		}

		this.intuitiveDiagramStore.markRecentlyEditedTeeth(updates.map(u => u.toothId));
	}

	override openModalForUnitTypeIfNeededInternal(indication: IntuitiveIndication): Observable<ToothModalResult> {
		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			return EMPTY;
		}

		if (indication === IntuitiveIndication.BridgeImplantBased) {
			const bridgeImplantBasedUnitType = toUnitTypeInBridge(indication);

			if (
				bridgeImplantBasedUnitType != null &&
				this.implantsViewService.shouldPreventImplantBasedInBridgeSelection(bridgeImplantBasedUnitType)
			) {
				return EMPTY;
			}

			const openParams = this.buildImplantsViewModalOpenParams(
				this.intuitiveDiagramQuery.selectedBridgeTeeth,
				IntuitiveIndication.BridgeImplantBased
			);

			return defer(() => {
				return this.implantsViewModalService.open(openParams).pipe(
					take(1),
					tap(result => {
						if (result.action === TOOTH_MODAL_ACTION.Save && result.payload.toothPartialUpdate != null) {
							this.updateBridgeTeethByIdsForIndicationWithModal(selectedToothIds, result.payload.toothPartialUpdate);

							this.markCrownControlsAsRemovedByUser(
								selectedToothIds,
								result.payload.pendingAdditionalInfoClears?.flatMap(entry => entry.clearedFields) ?? []
							);

							return;
						}

						if (result.action === TOOTH_MODAL_ACTION.Delete) {
							this.resetBridgeTeethToDefaultUnitType(selectedToothIds);
						}
					})
				);
			});
		}

		return EMPTY;
	}

	private updateBridgeTeethByIdsForIndicationWithModal(toothIds: number[], partialToothUpdate: Partial<Tooth>): void {
		const firstTooth = this.intuitiveDiagramQuery.selectedBridgeTeeth.find(toothInBridge =>
			toothIds.includes(toothInBridge.ToothID as number)
		);

		if (!firstTooth) {
			return;
		}

		/**
		 * We need to reset the fields that are not part of the partial update to avoid having stale values in the teeth.
		 * Because the teeth values are not reset to the default values when the modal is opened, we need to reset them on save action.
		 */
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { ToothID: toothId, ...resetFields } = getNewTooth({
			...partialToothUpdate,
			UnitTypeID: firstTooth.UnitTypeID,
			BridgeIndex: firstTooth.BridgeIndex,
			ToothInBridgeTypeID: firstTooth.ToothInBridgeTypeID
		});

		this.updateBridgeTeethByIds(toothIds, resetFields);
	}

	private resetBridgeTeethToDefaultUnitType(toothIds: number[]): void {
		if (toothIds.length === 0) {
			return;
		}

		const firstTooth = this.intuitiveDiagramQuery.selectedBridgeTeeth.find(toothInBridge =>
			toothIds.includes(toothInBridge.ToothID as number)
		);

		if (!firstTooth) {
			return;
		}

		const defaultUnitType = this.indicationsService.getDefaultUnitType();
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { ToothID: toothId, ...resetFields } = getNewTooth({
			UnitTypeID: defaultUnitType,
			BridgeIndex: firstTooth.BridgeIndex
		});

		this.updateBridgeTeethByIds(toothIds, resetFields);
	}

	private mapJawTeeth(
		jaw: Tooth[],
		selectedToothIds: number[],
		teethNumberingSystem: TeethNumberingSystem,
		bridges: Record<number, IntuitiveBridge>,
		selectedBridge: IntuitiveBridge,
		validationError: IntuitiveBridgeValidationErrors | undefined
	): IntuitiveToothForDiagram[] {
		const existingBridges = this.filterOriginalBridgeOut(bridges, selectedBridge);

		return jaw.map(t => {
			const { tooth: mappingTooth, bridge } = this.findToothBridge(t, selectedBridge, existingBridges);

			return {
				...this.toDiagramVM(mappingTooth, selectedToothIds, teethNumberingSystem, bridge, validationError),
				isDisabled: this.isToothDisabledForBridge(t, selectedBridge)
			};
		});
	}

	private toBridgeToothForTreatment(
		tooth: Tooth,
		teethNumberingSystem: TeethNumberingSystem,
		toothLastTouchedAt: Record<number, number>
	): IntuitiveToothForTreatment {
		return this.toothTreatmentMapper.toIntuitiveToothForTreatment(tooth, teethNumberingSystem, toothLastTouchedAt, true);
	}

	private findToothBridge(
		tooth: Tooth,
		selectedBridge: IntuitiveBridge | undefined,
		bridges: Record<number, IntuitiveBridge> | undefined
	) {
		const toothInSelectedBridge = selectedBridge?.teeth.find(bt => tooth.ToothID === bt.ToothID);

		if (toothInSelectedBridge) {
			return {
				tooth: toothInSelectedBridge,
				bridge: selectedBridge
			};
		}

		if (bridges && tooth.BridgeIndex != null) {
			return {
				tooth,
				bridge: bridges[tooth.BridgeIndex]
			};
		}

		return {
			tooth,
			bridge: undefined
		};
	}

	private addTeethInBridge(toothId: number, selectedBridge: IntuitiveBridge): void {
		const tooth = this.teethDiagramQuery.teethRecord[toothId];

		if (tooth?.BridgeIndex != null && tooth.BridgeIndex !== selectedBridge.index) {
			return;
		}

		const bridgeToothId = selectedBridge.outerLeftToothId ?? selectedBridge.outerRightToothId;

		if (bridgeToothId == null) {
			return;
		}

		const bridgeJaw = getJawByToothNumber({ toothId: bridgeToothId });
		const jaw = bridgeJaw === 'upper' ? this.teethDiagramQuery.teeth.upperJaw : this.teethDiagramQuery.teeth.lowerJaw;
		const teethToAdd = this.bridgeService.getTeethToExtendBridge(selectedBridge, toothId, jaw);

		if (!teethToAdd || teethToAdd.length === 0) {
			return;
		}

		const bridgeTeeth: Tooth[] = teethToAdd.map(t => ({
			...t,
			UnitTypeID: bridgeUnitType,
			BridgeIndex: selectedBridge.index
		}));

		const expandedBridge = this.bridgeService.buildExpandedBridge(selectedBridge, bridgeTeeth, jaw);

		const newToothIds = bridgeTeeth.map(t => t.ToothID).filter((id): id is number => id !== null);

		this.intuitiveDiagramStore.update({
			selectedBridge: expandedBridge
		});

		this.intuitiveDiagramStore.markRecentlyEditedTeeth(newToothIds);
	}

	private toggleInBridgeSelection(toothId: number): void {
		const selectedBridgeTeeth = this.intuitiveDiagramQuery.selectedBridgeTeeth;
		const bridgeTooth = selectedBridgeTeeth.find(t => t.ToothID === toothId);

		if (!bridgeTooth) {
			return;
		}

		const selectedToothIds = this.intuitiveDiagramQuery.selectedToothIds;

		let next: number[];

		if (selectedToothIds.includes(toothId)) {
			next = selectedToothIds.filter(id => id !== toothId);
		} else if (!this.validationService.isDefaultBridgeIndication(bridgeTooth)) {
			next = [toothId];
		} else {
			next = selectedToothIds.filter(id => {
				const bt = selectedBridgeTeeth.find(t => t.ToothID === id);

				return bt ? this.validationService.isDefaultBridgeIndication(bt) : false;
			});
			next = [...next, toothId];
		}

		this.intuitiveDiagramStore.update({ selectedToothIds: next });
	}

	private isToothDisabledForBridge(tooth: Tooth, selectedBridge: IntuitiveBridge): boolean {
		const bridgeToothId = selectedBridge.outerLeftToothId ?? selectedBridge.outerRightToothId;

		if (bridgeToothId == null || tooth.ToothID == null) {
			return false;
		}

		const toothJawType = getJawByToothNumber({ toothId: tooth.ToothID });
		const bridgeJawType = getJawByToothNumber({ toothId: bridgeToothId });

		if (toothJawType !== bridgeJawType) {
			return true;
		}

		if (selectedBridge.teeth.some(bt => bt.ToothID === tooth.ToothID)) {
			return false;
		}

		if (tooth.BridgeIndex != null && tooth.BridgeIndex !== selectedBridge.index) {
			return true;
		}

		return this.hasOtherBridgeBetweenToothAndSelectedBridge(tooth, selectedBridge, toothJawType);
	}

	private hasOtherBridgeBetweenToothAndSelectedBridge(
		tooth: Tooth,
		selectedBridge: IntuitiveBridge,
		jawType: 'upper' | 'lower'
	): boolean {
		const jaw = jawType === 'upper' ? this.teethDiagramQuery.teeth.upperJaw : this.teethDiagramQuery.teeth.lowerJaw;
		const toothIndex = jaw.findIndex(t => t.ToothID === tooth.ToothID);

		if (toothIndex === -1) {
			return false;
		}

		const leftIndex = selectedBridge.outerLeftToothId != null ? jaw.findIndex(t => t.ToothID === selectedBridge.outerLeftToothId) : -1;
		const rightIndex =
			selectedBridge.outerRightToothId != null ? jaw.findIndex(t => t.ToothID === selectedBridge.outerRightToothId) : -1;

		if (leftIndex - toothIndex > 0) {
			const teethBetween = jaw.slice(toothIndex + 1, leftIndex);

			return teethBetween.some(t => t.BridgeIndex != null && t.BridgeIndex !== selectedBridge.index);
		}

		if (toothIndex - rightIndex > 0) {
			const teethBetween = jaw.slice(rightIndex, toothIndex + 1);

			return teethBetween.some(t => t.BridgeIndex != null && t.BridgeIndex !== selectedBridge.index);
		}

		return false;
	}

	private filterOriginalBridgeOut(bridges: Record<number, IntuitiveBridge>, selectedBridge: IntuitiveBridge) {
		const bridgeEntries = Object.entries(bridges).filter(([, item]) => item.index !== selectedBridge.index);

		return Object.fromEntries(bridgeEntries);
	}
}
